import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface WebVitalsMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  id?: string;
  delta?: number;
}

interface EnhancedWebVitalsTrackerProps {
  enabled?: boolean;
  pageUrl?: string;
  onMetric?: (metric: WebVitalsMetric) => void;
}

/**
 * Enhanced Web Vitals Tracker with comprehensive performance monitoring
 * Tracks all Core Web Vitals and additional performance metrics
 */
const EnhancedWebVitalsTracker: React.FC<EnhancedWebVitalsTrackerProps> = ({
  enabled = true,
  pageUrl = window.location.pathname,
  onMetric
}) => {
  const [sessionId] = useState(() => crypto.randomUUID());
  const observersRef = useRef<PerformanceObserver[]>([]);
  const metricsRef = useRef<Record<string, number>>({});
  const [connectionInfo, setConnectionInfo] = useState<any>(null);

  // Performance thresholds for rating calculation
  const thresholds = {
    lcp: { good: 2500, poor: 4000 },
    fid: { good: 100, poor: 300 },
    cls: { good: 0.1, poor: 0.25 },
    fcp: { good: 1800, poor: 3000 },
    ttfb: { good: 800, poor: 1800 },
    inp: { good: 200, poor: 500 },
    fcpToLcp: { good: 1000, poor: 2500 }
  };

  const getRating = (name: string, value: number): 'good' | 'needs-improvement' | 'poor' => {
    const threshold = thresholds[name as keyof typeof thresholds];
    if (!threshold) return 'good';

    if (value <= threshold.good) return 'good';
    if (value <= threshold.poor) return 'needs-improvement';
    return 'poor';
  };

  const reportMetric = async (name: string, value: number, id?: string) => {
    // Avoid duplicate reporting
    if (metricsRef.current[name] === value && name !== 'cls') return;
    
    metricsRef.current[name] = value;
    const rating = getRating(name, value);
    
    const metric: WebVitalsMetric = { name, value, rating, id };
    
    // Log for debugging
    console.log(`🔍 ${name.toUpperCase()}: ${value}${name.includes('time') || name.includes('fcp') || name.includes('lcp') || name.includes('ttfb') ? 'ms' : ''} (${rating})`);
    
    // Call external handler
    if (onMetric) onMetric(metric);
    
    // Report to database
    if (enabled) {
      try {
        await supabase.from('performance_metrics').insert({
          session_id: sessionId,
          page_url: pageUrl,
          metric_name: name,
          metric_value: value,
          rating,
          user_agent: navigator.userAgent,
          connection_type: connectionInfo?.effectiveType || 'unknown',
          device_memory: (navigator as any)?.deviceMemory || null
        });
      } catch (error) {
        console.warn('Failed to report metric:', error);
      }
    }
  };

  useEffect(() => {
    if (!enabled) return;

    // Get connection info
    const connection = (navigator as any)?.connection;
    setConnectionInfo(connection);

    const cleanup = () => {
      observersRef.current.forEach(observer => {
        try {
          observer.disconnect();
        } catch (e) {
          // Ignore cleanup errors
        }
      });
      observersRef.current = [];
    };

    try {
      // Largest Contentful Paint (LCP)
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries() as PerformanceEventTiming[];
        const lastEntry = entries[entries.length - 1];
        if (lastEntry) {
          reportMetric('lcp', Math.round(lastEntry.startTime), 'lcp-element');
        }
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      observersRef.current.push(lcpObserver);

      // First Input Delay (FID)
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries() as PerformanceEventTiming[];
        entries.forEach((entry) => {
          const fid = entry.processingStart - entry.startTime;
          reportMetric('fid', Math.round(fid), entry.entryType);
        });
      });
      fidObserver.observe({ entryTypes: ['first-input'] });
      observersRef.current.push(fidObserver);

      // Interaction to Next Paint (INP) - New Core Web Vital
      const inpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries() as PerformanceEventTiming[];
        entries.forEach((entry) => {
          const inp = entry.processingStart - entry.startTime + (entry.processingEnd - entry.processingStart);
          reportMetric('inp', Math.round(inp), entry.entryType);
        });
      });
      try {
        inpObserver.observe({ entryTypes: ['event'] });
        observersRef.current.push(inpObserver);
      } catch (e) {
        // INP not supported in all browsers yet
      }

      // Cumulative Layout Shift (CLS)
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries() as PerformanceEventTiming[];
        entries.forEach((entry) => {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
            reportMetric('cls', Math.round(clsValue * 1000) / 1000);
          }
        });
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });
      observersRef.current.push(clsObserver);

      // First Contentful Paint (FCP)
      const fcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.name === 'first-contentful-paint') {
            reportMetric('fcp', Math.round(entry.startTime));
            
            // Calculate FCP to LCP delta when LCP is available
            if (metricsRef.current.lcp) {
              reportMetric('fcpToLcp', metricsRef.current.lcp - entry.startTime);
            }
          }
        });
      });
      fcpObserver.observe({ entryTypes: ['paint'] });
      observersRef.current.push(fcpObserver);

      // Time to First Byte (TTFB)
      const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      if (navigationEntries.length > 0) {
        const entry = navigationEntries[0];
        const ttfb = entry.responseStart - entry.requestStart;
        reportMetric('ttfb', Math.round(ttfb));
        
        // Additional navigation timing metrics
        reportMetric('domContentLoaded', Math.round(entry.domContentLoadedEventEnd - entry.fetchStart));
        reportMetric('loadComplete', Math.round(entry.loadEventEnd - entry.fetchStart));
        reportMetric('networkTime', Math.round(entry.responseEnd - entry.requestStart));
        reportMetric('serverTime', Math.round(entry.responseStart - entry.requestStart));
      }

      // Resource loading performance
      const resourceObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries() as PerformanceResourceTiming[];
        entries.forEach((entry) => {
          // Report slow resources
          if (entry.duration > 1000) {
            reportMetric(`slowResource_${entry.initiatorType}`, Math.round(entry.duration));
          }
          
          // Track critical resource types
          if (entry.name.includes('.css') || entry.name.includes('.js') || entry.name.includes('font')) {
            const resourceType = entry.name.includes('.css') ? 'css' : 
                               entry.name.includes('.js') ? 'js' : 'font';
            reportMetric(`${resourceType}LoadTime`, Math.round(entry.duration));
          }
        });
      });
      resourceObserver.observe({ entryTypes: ['resource'] });
      observersRef.current.push(resourceObserver);

      // Mark tracking start
      if (window.performance?.mark) {
        window.performance.mark('webvitals-tracking-start');
      }

    } catch (error) {
      console.warn('Enhanced Web Vitals tracking not fully supported:', error);
    }

    return cleanup;
  }, [enabled, pageUrl, onMetric, sessionId, connectionInfo]);

  // Component is invisible - only tracks metrics
  return null;
};

export default EnhancedWebVitalsTracker;